import React, { useEffect, useState } from "react";

/**
 * PerfOverlay - decoded memory, on the device itself, split by asset type.
 *
 * WHY THIS EXISTS. The games run on TVs with no devtools, and the failures
 * that matter there are memory failures: a decode that silently fails, a
 * texture the compositor drops, a tab the OS kills between rounds. The number
 * that predicts all three is not download size, it is DECODED size, and no
 * browser API reports decoded size split by asset type. So we reconstruct it.
 *
 * DROP-IN CONTRACT. One file. Imports React and nothing else. No SCSS module,
 * no shared constants, no AudioManager import - everything it reads is
 * feature-detected at runtime, so the same file works in a game with the 2026
 * AudioManager, a game with plain Howler, and a game with neither. Copy it to
 * `components/Shared/PerfOverlay/`, add two lines to Host.js (and Client.js if
 * you want it on the phone):
 *
 *     import PerfOverlay, { PERF_OVERLAY } from "components/Shared/PerfOverlay/PerfOverlay";
 *     ...
 *     {PERF_OVERLAY && <PerfOverlay />}
 *
 * ON by default. Flip PERF_OVERLAY below to false to hide it.
 * That also removes the PERF button from every player's phone (PerfToggle).
 *
 * WHERE IT SITS. Bottom-left, deliberately: AudioDebugOverlay owns
 * the top-right, and the two are meant to be readable at the same time.
 *
 * WHAT TO READ, in order:
 *   1. `nodes` - DOM nodes per minute. This is the leak signal, because it is
 *      the one that still works: a game that keeps adding nodes and never
 *      taking them away is the usual shape of a leak here, and a leak is what
 *      kills a TV on game four rather than game one.
 *   2. The four bucket rows - which asset type owns the footprint. That is the
 *      one that tells you where to spend the optimisation effort.
 *   3. `blind` - what this overlay could NOT see. Never read the totals
 *      without reading this line; see BLIND SPOTS at the bottom of the file.
 *   4. `drift` and `heap`, last and usually not at all. The browser caches
 *      performance.memory rather than reporting it live, so this normally
 *      reads "heap frozen". When it does show a number, above +2MB/min is a
 *      leak. See BLIND SPOTS note 4 before trusting it.
 *
 * EVERY BYTE HERE IS AN ESTIMATE. The estimates are built to be UPPER bounds,
 * because a memory budget you round up is safe and one you round down is not.
 * Each one says how it was derived next to the code that derives it.
 */

/** Show the overlay. Set to false to hide it. */
export const PERF_OVERLAY = false;

/* ------------------------------------------------------------------ */
/* Tunables                                                            */
/* ------------------------------------------------------------------ */

/** Cheap sample: heap, howls, videos, <img> walk. */
const SAMPLE_MS = 2000;

/** Expensive sample: getComputedStyle sweep for CSS background images. */
const DEEP_SCAN_MS = 10000;

/** Samples kept for the drift fit. 90 x 2s = 3 minutes, about one game round. */
const HISTORY = 90;

/**
 * Decoded video frames a browser holds at once. Chromium keeps a handful in
 * flight between the decoder and the compositor and does not report how many,
 * so this is the assumption the video number rests on. Four is conservative
 * for a single 1080p element; raise it if you are running several at once.
 */
const VIDEO_FRAMES_IN_FLIGHT = 4;

/** Heap use, as a percentage of the limit, above which the line turns red. */
const HEAP_WARN_PCT = 70;

/**
 * Drift, in bytes per minute, above which the drift readout turns red.
 *
 * 2MB/min sits well clear of the fit's own noise. Fed 90 synthetic samples of
 * a known +3.00MB/min leak buried under an 8MB GC sawtooth, the fit returned
 * +3.29; fed a flat heap with the same sawtooth it returned +0.29. So treat
 * anything under about 0.5MB/min as zero, and only believe a number that
 * holds up over a few minutes.
 */
const DRIFT_WARN_BYTES_MIN = 2 * 1024 * 1024;

/**
 * DOM nodes per minute above which the node drift turns red.
 *
 * This is the leak readout that actually works, because it does not depend on
 * performance.memory. A game host page settles around a few hundred nodes; a
 * round that adds 20 a minute and never takes them away has added a thousand
 * by the end of a session. Measured on a Tizen set: 384 nodes climbing to 458
 * across 5.5 minutes, which is this line reading about +13/min while the heap
 * readout sat frozen and claimed nothing was happening.
 */
const NODE_DRIFT_WARN_PER_MIN = 15;

/** How long each fps measurement window runs, and how often one starts. */
const FPS_WINDOW_MS = 1000;
const FPS_PERIOD_MS = 10000;

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const MB = 1048576;
const mb = (bytes) => `${(bytes / MB).toFixed(1)}MB`;

/**
 * Run a measurement, and swallow whatever it throws.
 *
 * The rule this file lives by: a diagnostic must never be the thing that
 * breaks the game. Everything here reads objects it does not own, so any
 * reader can throw for reasons that have nothing to do with us. The first
 * failure per reader is logged once and then silenced, because a reader that
 * throws tends to throw on every tick and a console full of one repeated
 * message is how a real error gets missed.
 */
const warned = new Set();
function safe(fn, fallback) {
    try {
        return fn();
    } catch (err) {
        const key = (fn.name || "anonymous") + ":" + (err && err.message);
        if (!warned.has(key)) {
            warned.add(key);
            console.warn("[perf] measurement failed, reporting empty:", key);
        }
        return fallback;
    }
}

// ES2020 features (matchAll, flat, fromEntries) are avoided throughout: the
// oldest Tizen sets in the estate run Chromium 63 and react-scripts does not
// polyfill by default, so anything newer than ES2018 is a blank screen there.

const EXT_RE = /\.([a-z0-9]{2,4})(?:[?#]|$)/i;
const AUDIO_EXT = /^(mp3|ogg|wav|m4a|aac|opus|flac)$/;
const VIDEO_EXT = /^(mp4|webm|mov|m4v|ogv)$/;
const IMAGE_EXT = /^(png|jpg|jpeg|gif|webp|svg|avif|bmp|ico)$/;
const SCRIPT_EXT = /^(js|mjs)$/;

/**
 * Which bucket a network resource belongs to.
 *
 * Extension first, initiatorType second. That order is deliberate: webpack
 * hashes filenames but keeps the extension, whereas initiatorType lies about
 * the things we care about most - Howler fetches a clip over XHR for Web Audio,
 * so an mp3 arrives tagged "xmlhttprequest", and an image referenced from CSS
 * arrives tagged "css".
 */
function bucketFor(entry) {
    const m = EXT_RE.exec(entry.name);
    const ext = m ? m[1].toLowerCase() : "";
    if (AUDIO_EXT.test(ext)) return "audio";
    if (VIDEO_EXT.test(ext)) return "video";
    if (IMAGE_EXT.test(ext)) return "image";
    if (SCRIPT_EXT.test(ext)) return "script";
    const t = entry.initiatorType;
    if (t === "img" || t === "css") return "image";
    if (t === "script") return "script";
    if (t === "audio") return "audio";
    if (t === "video") return "video";
    return "other";
}

/** Strip a webpack hash so a clip name is readable from the sofa. */
function shortName(url) {
    if (!url) return "(none)";
    const file = String(url).split("?")[0].split("/").pop() || String(url);
    return file.replace(/\.[0-9a-f]{8,}(?=\.)/, "");
}

/* ------------------------------------------------------------------ */
/* Image dimension cache                                               */
/* ------------------------------------------------------------------ */

/**
 * url -> {w, h}, or null while a probe is in flight.
 *
 * A CSS background gives us a URL and no dimensions, and decoded cost is
 * entirely a function of dimensions, so we have to ask the browser. We ask by
 * loading the URL into a detached Image - it is already in the HTTP cache, so
 * this costs no network - then read naturalWidth/Height and immediately point
 * that Image at a 1x1 pixel so our probe is not itself holding a decoded
 * bitmap alive. Measuring memory must not add memory.
 */
const imageDims = new Map();

const BLANK_PIXEL =
    "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";

function probeImage(url) {
    if (!url || imageDims.has(url)) return;
    imageDims.set(url, null);
    const img = new Image();
    const finish = (w, h) => {
        imageDims.set(url, { w: w, h: h });
        img.onload = null;
        img.onerror = null;
        img.src = BLANK_PIXEL; // detach from the decoded bitmap
    };
    img.onload = () => finish(img.naturalWidth, img.naturalHeight);
    img.onerror = () => finish(0, 0);
    img.src = url;
}

/**
 * Decoded bytes for a bitmap of these dimensions.
 *
 * Browsers hold a decoded image as 32-bit RGBA, one allocation of w x h x 4,
 * whatever the source format was. A 4K background PNG that downloads as 800KB
 * is 33MB resident. That gap is the single most common reason a TV runs out of
 * memory, and it is why this overlay exists.
 */
const bitmapBytes = (d) => (d && d.w ? d.w * d.h * 4 : 0);

/* ------------------------------------------------------------------ */
/* The profiler                                                        */
/* ------------------------------------------------------------------ */

class MemoryProfiler {
    constructor() {
        this.snapshot = emptySnapshot();
        this.history = [];      // {t, heap, audio, video, image}
        this.marks = [];        // {label, t, heap}
        this.peakHeap = 0;
        this.peak = { audio: 0, video: 0, image: 0, script: 0 };
        this.listeners = new Set();
        this.longTasks = 0;
        this.frames = 0;
        this.fps = 0;
        this.transfer = {};     // bucket -> {count, encoded, decoded}
        this.opaque = 0;        // cross-origin entries with no size data
        this.startedAt = Date.now();
        this._timers = [];
        this._running = false;
        this._consumers = 0;
        this._passiveStarted = false;
        this._bgUrls = new Set();
    }

    /* ---- lifecycle ---- */

    /**
     * Begin sampling for one consumer. Reference counted.
     *
     * WHY REFERENCE COUNTED. The host mounts the overlay permanently, but the
     * client mounts it behind a button (PerfToggle) precisely so the expensive
     * work is not running on a player's phone during a game. An earlier
     * version of this file never stopped on unmount, which meant that once a
     * player opened the panel once, the 2s sample and the 10s getComputedStyle
     * sweep ran for the rest of the session with nothing on screen - the exact
     * cost the button exists to avoid, and invisible because the panel looked
     * shut. Counting consumers is what makes closing the panel actually close it.
     *
     * The passive observers are started once and never stopped: they only
     * accumulate resource-timing and long-task entries as the browser hands
     * them over, and disconnecting one would silently lose entries and
     * understate the script total for the rest of the session.
     *
     * History, peaks and transfer totals survive a stop, so reopening the
     * panel resumes the drift fit rather than restarting it.
     */
    start() {
        this._consumers++;

        if (!this._passiveStarted) {
            this._passiveStarted = true;
            this._observeResources();
            this._observeLongTasks();
        }

        // Already running for someone else: just refresh, so a panel that has
        // only now been opened shows current numbers instead of whatever was
        // on screen when it was last shut.
        if (this._running) {
            this.sample();
            return;
        }

        this._running = true;
        this._countFrames();
        safe(() => this.deepScan(), null);
        this.sample();
        this._timers.push(setInterval(() => this.sample(), SAMPLE_MS));
        this._timers.push(setInterval(() => safe(() => this.deepScan(), null), DEEP_SCAN_MS));
    }

    /**
     * One consumer has gone away. When the last one does, the expensive work
     * stops: the sample interval, the deep scan, and the per-frame counter.
     * Everything measured so far is kept.
     */
    release() {
        this._consumers = Math.max(0, this._consumers - 1);
        if (this._consumers > 0 || !this._running) return;

        this._running = false;
        this._timers.forEach(clearInterval);
        this._timers = [];
        if (this._raf) cancelAnimationFrame(this._raf);
        this._raf = null;
        if (this._fpsTimer) clearTimeout(this._fpsTimer);
        this._fpsTimer = null;
        this.fps = 0;
    }

    /** Hard stop, including the passive observers. For `__perf.stop()`. */
    stop() {
        this._consumers = 0;
        this.release();
        this._passiveStarted = false;
        if (this._resourceObserver) this._resourceObserver.disconnect();
        if (this._longTaskObserver) this._longTaskObserver.disconnect();
    }

    subscribe(fn) {
        this.listeners.add(fn);
        return () => this.listeners.delete(fn);
    }

    /**
     * Label a moment so you can read the heap delta across it later, from a
     * console: `__perf.mark("q5 start")`, play the round, `__perf.summary()`.
     * Games can call it from a round transition to get the same thing on a TV
     * with no console attached.
     */
    mark(label) {
        const heap = heapBytes().used;
        this.marks.push({ label: label, t: Date.now() - this.startedAt, heap: heap });
        if (this.marks.length > 20) this.marks.shift();
        return { label: label, heap: mb(heap) };
    }

    /* ---- network side: what was downloaded, by type ---- */

    _observeResources() {
        // A PerformanceObserver rather than getEntriesByType, because the
        // resource timing buffer holds 250 entries by default and a game that
        // streams audio blows past that mid-session. The observer accumulates
        // into our own totals, so an overflowed buffer costs us nothing.
        const take = (entries) => {
            for (let i = 0; i < entries.length; i++) {
                const e = entries[i];
                const bucket = bucketFor(e);
                const row = this.transfer[bucket] || (this.transfer[bucket] = { count: 0, encoded: 0, decoded: 0 });
                row.count++;
                row.encoded += e.encodedBodySize || 0;
                row.decoded += e.decodedBodySize || 0;
                // A cross-origin response without Timing-Allow-Origin reports
                // every size as 0. Counting those separately is the difference
                // between "scripts are small" and "we cannot see the scripts".
                if (!e.encodedBodySize && e.transferSize === 0) this.opaque++;
            }
        };
        if (typeof PerformanceObserver === "function") {
            try {
                this._resourceObserver = new PerformanceObserver((list) => take(list.getEntries()));
                this._resourceObserver.observe({ type: "resource", buffered: true });
                return;
            } catch (err) {
                /* falls through to the one-shot read below */
            }
        }
        if (performance.getEntriesByType) take(performance.getEntriesByType("resource"));
    }

    _observeLongTasks() {
        if (typeof PerformanceObserver !== "function") return;
        try {
            this._longTaskObserver = new PerformanceObserver((list) => {
                this.longTasks += list.getEntries().length;
            });
            this._longTaskObserver.observe({ entryTypes: ["longtask"] });
        } catch (err) {
            // Not supported (Safari, older WebViews). The row reads "-".
            this._longTaskObserver = null;
        }
    }

    /**
     * Measure fps on a duty cycle, not continuously.
     *
     * This used to hold a permanent rAF loop. On a TV that turns out to be a
     * real cost rather than a notional one: a Tizen set in the estate fires
     * rAF at about 751Hz (unthrottled, against 60Hz on a Fire TV), so a
     * per-frame callback that exists only to count runs 751 times a second,
     * for the whole session, on the weakest device we have. The overlay is
     * always mounted on the host, so nothing ever stood that loop down.
     *
     * Measuring must not become the load. So: count frames for one second,
     * then stand down for nine. The reading is a second old at worst, which is
     * fine for a number nobody reads faster than that.
     */
    _countFrames() {
        const measure = () => {
            let frames = 0;
            const start = performance.now();
            const tick = (now) => {
                frames++;
                const elapsed = now - start;
                if (elapsed >= FPS_WINDOW_MS) {
                    this.fps = Math.round((frames * 1000) / elapsed);
                    this._raf = null;
                    this._fpsTimer = setTimeout(measure, FPS_PERIOD_MS - FPS_WINDOW_MS);
                    return;
                }
                this._raf = requestAnimationFrame(tick);
            };
            this._raf = requestAnimationFrame(tick);
        };
        measure();
    }

    /* ---- the expensive sweep ---- */

    /**
     * Find every CSS background image on the page.
     *
     * There is no API for "images this document references", so the only way
     * is getComputedStyle on every element. A game host page is a few hundred
     * elements and this costs a few milliseconds on a desktop, tens on a TV,
     * which is why it runs on its own slow timer and not on the sample tick.
     */
    deepScan() {
        const els = document.getElementsByTagName("*");
        const urlRe = /url\((['"]?)(.*?)\1\)/g;
        for (let i = 0; i < els.length; i++) {
            const bg = getComputedStyle(els[i]).backgroundImage;
            if (!bg || bg === "none") continue;
            let m;
            urlRe.lastIndex = 0;
            while ((m = urlRe.exec(bg)) !== null) {
                const url = m[2];
                if (!url) continue;
                this._bgUrls.add(url);
                probeImage(url);
            }
        }
    }

    /* ---- the cheap sample ---- */

    sample() {
        // Every measurement is guarded individually.
        //
        // This is instrumentation mounted on the host AND the client, so a
        // throw anywhere in here would take the game down rather than cost us
        // a diagnostic - the worst possible failure mode for a hardening tool.
        // Each reader touches something it does not own (a game's AudioManager,
        // a media element mid-teardown, a cross-origin stylesheet that makes
        // getComputedStyle throw), so any of them can fail without warning.
        // A bucket that throws reads as its empty value and the other three
        // keep reporting.
        const blank = emptySnapshot();
        const heap = safe(heapBytes, blank.heap);
        const audio = safe(measureAudio, blank.audio);
        const video = safe(measureVideo, blank.video);
        const image = safe(() => measureImages(this._bgUrls), blank.image);
        const script = safe(() => measureScripts(this.transfer), blank.script);

        this.peakHeap = Math.max(this.peakHeap, heap.used);
        this.peak.audio = Math.max(this.peak.audio, audio.bytes);
        this.peak.video = Math.max(this.peak.video, video.bytes);
        this.peak.image = Math.max(this.peak.image, image.bytesAll);
        this.peak.script = Math.max(this.peak.script, script.bytes);

        const dom = safe(domNodeCount, 0);

        this.history.push({
            t: Date.now() - this.startedAt,
            heap: heap.used,
            audio: audio.bytes,
            video: video.bytes,
            image: image.bytesAll,
            dom: dom,
        });
        if (this.history.length > HISTORY) this.history.shift();

        this.snapshot = {
            heap: heap,
            peakHeap: this.peakHeap,
            peak: this.peak,
            // Heap drift is suppressed when the reading is frozen, see
            // heapIsStale. nodeDrift does not depend on performance.memory at
            // all, so it is the leak signal that survives on a real TV: a game
            // that keeps adding DOM and never taking it away is the common
            // shape of a leak here, and document.getElementsByTagName is
            // always honest.
            heapStale: this.heapIsStale(),
            drift: this.heapIsStale() ? null : this.drift("heap"),
            nodeDrift: this.drift("dom"),
            audio: audio,
            video: video,
            image: image,
            script: script,
            wasm: safe(measureWasm, 0),
            dom: dom,
            fps: this.fps,
            longTasks: this._longTaskObserver ? this.longTasks : null,
            opaque: this.opaque,
            device: safe(deviceInfo, {}),
            uptimeMs: Date.now() - this.startedAt,
        };

        // A subscriber that throws must not stop the others being told, and
        // must not propagate back into the interval that called us.
        this.listeners.forEach((fn) => safe(() => fn(this.snapshot), null));
    }

    /**
     * Heap trend in bytes per minute, by least squares over the sample window.
     *
     * A single before/after reading cannot tell a leak from a GC that has not
     * run yet - the heap saws up and down by tens of MB between collections.
     * A fit over three minutes of samples sees through the saw to the slope,
     * and the slope is the thing that decides whether the game survives an
     * hour in a pub.
     */
    drift(key) {
        const h = this.history;
        if (h.length < 10) return null;
        let sx = 0, sy = 0, sxy = 0, sxx = 0;
        for (let i = 0; i < h.length; i++) {
            const x = h[i].t / 60000; // minutes
            const y = h[i][key];
            sx += x; sy += y; sxy += x * y; sxx += x * x;
        }
        const n = h.length;
        const denom = n * sxx - sx * sx;
        if (!denom) return null;
        return (n * sxy - sx * sy) / denom;
    }

    /**
     * Has the heap reading moved at all across the window?
     *
     * CONFIRMED: `performance.memory` is deliberately degraded as an
     * anti-side-channel measure. It is quantised to 100KB from Chrome 86, and
     * is only reported precisely when the browser was started with
     * --enable-precise-memory-info. That is universal Chromium, not a device
     * fault, so it applies to the TVs and to phones equally.
     *
     * NOT CONFIRMED: the exact cache period. Blink is widely described as
     * holding the value for a long interval (20 minutes is the figure usually
     * quoted), but I could not find that in a primary source, so the overlay
     * does not state a number anywhere a user reads it. What is not in doubt
     * is the observed behaviour, which is what this function keys off.
     *
     * A fit over a value the browser is not updating is a fit over a
     * constant, and it returns a confident -0.0MB/min. Observed on a
     * Tizen set: bit-identical heap across 5.5 minutes while the DOM grew 19%
     * and 224 long tasks fired. Reporting no leak there would be a lie, and a
     * lie in the reassuring direction, so when the value has not moved the
     * overlay says so instead of quoting a number from it.
     */
    heapIsStale() {
        const h = this.history;
        if (h.length < 10) return false;
        const first = h[0].heap;
        if (!first) return false;
        for (let i = 1; i < h.length; i++) if (h[i].heap !== first) return false;
        return true;
    }

    /** Console-friendly dump: `__perf.summary()`. */
    summary() {
        const s = this.snapshot;
        return {
            heap: `${mb(s.heap.used)} / ${mb(s.heap.limit)} (peak ${mb(s.peakHeap)})`,
            drift: s.heapStale ? "heap frozen (browser caches this value; not live)" : s.drift === null ? "settling" : `${(s.drift / MB).toFixed(2)}MB/min`,
            nodes: s.nodeDrift === null ? "settling" : `${s.nodeDrift.toFixed(0)} nodes/min`,
            audio: `${mb(s.audio.bytes)} in ${s.audio.decoded} decoded, ${s.audio.streaming} streaming`,
            video: `${mb(s.video.bytes)} across ${s.video.elements} element(s)`,
            image: `${mb(s.image.bytesVisible)} visible / ${mb(s.image.bytesAll)} known, ${s.image.count} unique`,
            script: `${mb(s.script.bytes)} source over ${s.script.count} files`,
            marks: this.marks.map((m) => `${(m.t / 1000).toFixed(0)}s ${m.label} ${mb(m.heap)}`),
            blindSpots: blindSpots(s),
        };
    }
}

function emptySnapshot() {
    return {
        heap: { used: 0, total: 0, limit: 0, available: false },
        peakHeap: 0,
        peak: { audio: 0, video: 0, image: 0, script: 0 },
        heapStale: false,
        drift: null,
        nodeDrift: null,
        audio: { bytes: 0, budget: 0, decoded: 0, streaming: 0, loading: 0, failed: 0, source: "none" },
        video: { bytes: 0, frameBytes: 0, bufferedBytes: 0, elements: 0, detail: [] },
        image: { bytesAll: 0, bytesVisible: 0, count: 0, visible: 0, pending: 0 },
        script: { bytes: 0, encoded: 0, count: 0 },
        wasm: 0,
        dom: 0,
        fps: 0,
        longTasks: null,
        opaque: 0,
        device: {},
        uptimeMs: 0,
    };
}

/* ------------------------------------------------------------------ */
/* Measurements                                                        */
/* ------------------------------------------------------------------ */

/**
 * JS heap.
 *
 * `performance.memory` is a non-standard Chromium extension. It is the only
 * per-process memory number available without cross-origin isolation - the
 * standard `measureUserAgentSpecificMemory()` requires COOP/COEP headers that
 * these apps do not send, so it is not an option here. If the property is
 * absent (Safari, Firefox, some WebViews) the overlay says so rather than
 * showing a zero, because "unavailable" and "empty" are different diagnoses.
 */
function heapBytes() {
    const m = performance && performance.memory;
    if (!m) return { used: 0, total: 0, limit: 0, available: false };
    return {
        used: m.usedJSHeapSize || 0,
        total: m.totalJSHeapSize || 0,
        limit: m.jsHeapSizeLimit || 0,
        available: true,
    };
}

/**
 * Decoded audio (PCM).
 *
 * Preferred source is the 2026 AudioManager's own `cacheStats()` - it knows
 * which clips it streamed and which it decoded. Where that does not exist we
 * read Howler directly: `Howler._howls` is the live registry, `_webAudio` says
 * the clip became an AudioBuffer rather than an <audio> stream, and `_duration`
 * is the decoded buffer's length. Deduped by `_src` because Howler's internal
 * cache is keyed by URL, so two Howls on one file share one buffer.
 *
 * Bytes = duration x sampleRate x 4 x 2. Web Audio resamples to the context
 * rate and stores float32 per channel, so compression ratio is irrelevant and
 * only duration and channel count matter - a 640KB, 32s stereo bed is ~12MB
 * resident. Channel count is not exposed by Howler, so we assume stereo; a
 * mono clip therefore reads 2x high, which is the safe direction for a budget.
 */
function measureAudio() {
    // Path 1: a game with the 2026 AudioManager.
    const mgr = window.__audioManager || (window.audio && window.audio.cacheStats ? window.audio : null);
    if (mgr && typeof mgr.cacheStats === "function") {
        const s = mgr.cacheStats();
        return {
            bytes: s.decodedBytes || 0,
            budget: s.budgetBytes || 0,
            decoded: s.loaded || 0,
            streaming: s.streaming || 0,
            loading: s.loading || 0,
            failed: s.failed || 0,
            source: "AudioManager",
        };
    }

    // Path 2: plain Howler. Howler assigns itself onto webpack's `global`
    // shim, and that shim resolves to globalThis (verified in a built CRA
    // bundle: `n.g=function(){if("object"===typeof globalThis)return
    // globalThis;...}`), so `window.Howler` is really there after any
    // `import { Howl } from "howler"` in these apps.
    const H = window.Howler;
    if (!H || !H._howls) {
        return { bytes: 0, budget: 0, decoded: 0, streaming: 0, loading: 0, failed: 0, source: "none" };
    }
    const rate = (H.ctx && H.ctx.sampleRate) || 44100;
    const seen = new Set();
    let bytes = 0, decoded = 0, streaming = 0, loading = 0;
    for (let i = 0; i < H._howls.length; i++) {
        const howl = H._howls[i];
        const src = Array.isArray(howl._src) ? howl._src[0] : howl._src;
        if (howl._state === "loading") loading++;
        if (howl._html5) { streaming++; continue; }
        if (howl._state !== "loaded" || !howl._webAudio) continue;
        if (seen.has(src)) continue;
        seen.add(src);
        decoded++;
        bytes += Math.round((howl._duration || 0) * rate * 4 * 2);
    }
    return { bytes: bytes, budget: 0, decoded: decoded, streaming: streaming, loading: loading, failed: 0, source: "Howler" };
}

/**
 * Video.
 *
 * Two separate costs, reported separately because they have different fixes.
 *
 * FRAMES: the decoder holds raw frames in 4:2:0 planar, 1.5 bytes per pixel,
 * several in flight at once. A single 1080p element is roughly 3MB per frame
 * and ~12MB resident. The fix for that number is resolution.
 *
 * BUFFERED: the compressed data the element has pulled ahead. Taken from
 * `video.buffered` against a bitrate derived from resource timing. The fix for
 * that number is preload policy, not resolution.
 *
 * Only elements attached to the document are visible to us; see BLIND SPOTS.
 */
function measureVideo() {
    const vids = document.getElementsByTagName("video");
    let frameBytes = 0, bufferedBytes = 0;
    const detail = [];
    for (let i = 0; i < vids.length; i++) {
        const v = vids[i];
        const w = v.videoWidth || 0;
        const h = v.videoHeight || 0;
        const perFrame = w * h * 1.5;
        const frames = perFrame * VIDEO_FRAMES_IN_FLIGHT;
        frameBytes += frames;

        let secs = 0;
        try {
            for (let b = 0; b < v.buffered.length; b++) secs += v.buffered.end(b) - v.buffered.start(b);
        } catch (err) {
            secs = 0;
        }
        // CLAMP TO THE MEDIA'S OWN DURATION. You cannot have more of a file
        // buffered than the file lasts, but a TV player is under no obligation
        // to agree: a Fire TV reported ranges that worked out at ~3e12 seconds,
        // which rendered as 484239191825.2MB of video and made the whole line
        // useless. Trusting buffered.end() unguarded is the bug; an element
        // that reports rubbish should read 0, not astronomy.
        const dur = isFinite(v.duration) && v.duration > 0 ? v.duration : 0;
        if (!isFinite(secs) || secs < 0) secs = 0;
        if (dur && secs > dur) secs = dur;

        const bitrate = bitrateFor(v.currentSrc, v.duration);
        // HARD CAP, because clamping the inputs was not enough. The clamp
        // bounds this to the file's reported transfer size, which is only
        // useful if that size is sane, and on a Roku it is not: the row still
        // read 484239191825.2MB with the clamp in place, so resource timing
        // there is handing back a nonsense encodedBodySize. No media element
        // holds a quarter of a gigabyte of compressed audio and video buffered,
        // so anything past that is the device talking nonsense and reads 0.
        let buffered = Math.round((secs * bitrate) / 8);
        if (!isFinite(buffered) || buffered < 0 || buffered > MAX_BUFFERED_BYTES) buffered = 0;
        bufferedBytes += buffered;

        detail.push({
            name: shortName(v.currentSrc),
            res: w && h ? `${w}x${h}` : "no frame",
            bytes: frames + buffered,
            buffered: secs,
            // Chromium-only cumulative counter: compressed bytes this element
            // has pushed through the decoder since it was created. Not resident
            // memory - it is a measure of decoder work, useful for spotting an
            // element that is quietly looping something in the background.
            pushed: typeof v.webkitVideoDecodedByteCount === "number" ? v.webkitVideoDecodedByteCount : null,
        });
    }
    return {
        bytes: frameBytes + bufferedBytes,
        frameBytes: frameBytes,
        bufferedBytes: bufferedBytes,
        elements: vids.length,
        detail: detail,
    };
}

/** Shortest clip we will compute a bitrate for. Below this the maths is noise. */
const MIN_MEDIA_SECONDS = 0.1;

/** Most a single element could plausibly hold buffered. Past this, disbelieve it. */
const MAX_BUFFERED_BYTES = 256 * 1024 * 1024;

/** Bits per second for a media URL, from what resource timing saw arrive. */
function bitrateFor(url, duration) {
    // MIN_MEDIA_SECONDS, not just non-zero: `!duration` catches 0 but not 1e-9,
    // and dividing a file size by that produces a bitrate with no physical
    // meaning. Anything shorter than this is not a clip we can reason about.
    if (!url || !duration || !isFinite(duration) || duration < MIN_MEDIA_SECONDS) return 0;
    if (!performance.getEntriesByName) return 0;
    const entries = performance.getEntriesByName(url);
    for (let i = 0; i < entries.length; i++) {
        const size = entries[i].encodedBodySize || 0;
        if (size) return (size * 8) / duration;
    }
    return 0;
}

/**
 * Decoded images.
 *
 * Split into VISIBLE and ALL on purpose. Chromium discards the decoded bitmap
 * for an image that has been off-screen for a while and re-decodes it on the
 * way back in, so ALL is the ceiling - what the page costs if everything were
 * decoded at once - and VISIBLE is the floor it is certainly paying now. When
 * the two are far apart, the page is buying its headroom with re-decodes, and
 * re-decodes on a TV are the dropped frames you see on a screen transition.
 */
function measureImages(bgUrls) {
    const seen = new Map(); // url -> {bytes, visible}

    const imgs = document.getElementsByTagName("img");
    for (let i = 0; i < imgs.length; i++) {
        const img = imgs[i];
        const url = img.currentSrc || img.src;
        if (!url) continue;
        const bytes = img.naturalWidth ? img.naturalWidth * img.naturalHeight * 4 : 0;
        const prev = seen.get(url);
        const visible = isOnScreen(img);
        seen.set(url, {
            bytes: Math.max(bytes, prev ? prev.bytes : 0),
            visible: visible || Boolean(prev && prev.visible),
        });
        if (!imageDims.has(url) && img.naturalWidth) {
            imageDims.set(url, { w: img.naturalWidth, h: img.naturalHeight });
        }
    }

    // CSS backgrounds found by the deep scan. We cannot cheaply tell which
    // element still carries each one, so they count toward ALL and not VISIBLE.
    let pending = 0;
    bgUrls.forEach((url) => {
        if (seen.has(url)) return;
        const d = imageDims.get(url);
        if (d === null || d === undefined) { pending++; return; }
        seen.set(url, { bytes: bitmapBytes(d), visible: false });
    });

    let bytesAll = 0, bytesVisible = 0, visible = 0;
    seen.forEach((v) => {
        bytesAll += v.bytes;
        if (v.visible) { bytesVisible += v.bytes; visible++; }
    });

    return {
        bytesAll: bytesAll,
        bytesVisible: bytesVisible,
        count: seen.size,
        visible: visible,
        pending: pending,
    };
}

function isOnScreen(el) {
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    return r.bottom > 0 && r.right > 0 && r.top < window.innerHeight && r.left < window.innerWidth;
}

/**
 * Scripts.
 *
 * `decodedBodySize` is the uncompressed SOURCE size - the bytes the parser
 * saw, not the bytes the engine kept. The engine's own cost (compiled code,
 * closures, every object the game has allocated) is the JS heap on the top
 * line. Both matter: source size is what a cold TV spends its first eight
 * seconds parsing, heap is what kills it later.
 */
function measureScripts(transfer) {
    const row = transfer.script || { count: 0, encoded: 0, decoded: 0 };
    return { bytes: row.decoded, encoded: row.encoded, count: row.count };
}

/**
 * WebAssembly heap, for the Unity games (PlayingWithFire, PokerNight, Pool).
 *
 * UNVERIFIED: this reads `window.unityInstance.Module.HEAP8`, which is where a
 * classic Unity loader puts it. react-unity-webgl v9 keeps its instance inside
 * the React component and may not expose it globally, in which case this reads
 * 0 and the row is hidden. If a Unity game needs the number, have it assign
 * `window.unityInstance` on load and this picks it up. Untested against a real
 * Unity build - treat a non-zero reading as a starting point, not a fact.
 */
function measureWasm() {
    const inst = window.unityInstance || window.gameInstance;
    const heap = inst && inst.Module && inst.Module.HEAP8;
    return heap && heap.byteLength ? heap.byteLength : 0;
}

function domNodeCount() {
    return document.getElementsByTagName("*").length;
}

function deviceInfo() {
    return {
        ram: navigator.deviceMemory || null,   // GB, Chromium only, coarse
        cores: navigator.hardwareConcurrency || null,
        label: deviceLabel(),
        ua: navigator.userAgent || "",
        viewport: viewportLabel(),
    };
}

/**
 * A short name for the device, pulled out of the user agent.
 *
 * WHY THIS IS ON THE PANEL. A photo of an overlay is only evidence if you know
 * which box produced it, and you cannot tell from the screen: three of our
 * sets are the same physical television with different sticks plugged in, and
 * a 0.5GB reading got filed against the wrong device for exactly that reason.
 * The label makes every screenshot self-identifying.
 *
 * Best effort, and the raw UA is printed underneath it precisely because this
 * is a guess. If the two disagree, believe the UA.
 */
function deviceLabel() {
    const ua = navigator.userAgent || "";
    const bits = [];

    // Fire TV model codes run from AFTSS to AFTKADE001. A {2,6} range was
    // too tight and a built-in set fell through to a bare "Android", on the
    // device the label was most needed on.
    const aft = ua.match(/\b(AFT[A-Z0-9]{2,12})\b/);
    const tizen = ua.match(/Tizen[ /]?([\d.]+)?/i);
    if (aft) bits.push("FireTV " + aft[1]);
    else if (/Roku/i.test(ua)) bits.push("Roku");
    else if (tizen) bits.push("Tizen" + (tizen[1] ? " " + tizen[1] : ""));
    else if (/Web0S|webOS/i.test(ua)) bits.push("webOS");
    else if (/PlayStation/i.test(ua)) bits.push("PlayStation");
    else if (/Android/i.test(ua)) bits.push("Android");

    const android = ua.match(/Android (\d+)/);
    if (android && !bits.length) bits.push("Android " + android[1]);

    const chrome = ua.match(/(?:Chrome|Chromium)\/(\d+)/);
    if (chrome) bits.push("Chrome " + chrome[1]);

    return bits.length ? bits.join(" · ") : "unknown device";
}

/**
 * CSS viewport and pixel ratio.
 *
 * Worth showing next to the label: a Fire TV reported a narrower CSS viewport
 * than a 1920 Tizen and clipped an overlay that fitted fine on the other, so
 * "it is a TV" says nothing useful about how much room there actually is.
 */
function viewportLabel() {
    if (typeof window === "undefined") return "";
    const dpr = window.devicePixelRatio || 1;
    return window.innerWidth + "x" + window.innerHeight + (dpr !== 1 ? " @" + dpr : "");
}

export { deviceLabel };

/**
 * What the numbers above could not see. Rendered on its own line because a
 * total without its blind spots is worse than no total: it reads as complete.
 */
function blindSpots(s) {
    const out = [];
    if (!s.heap.available) out.push("no performance.memory on this browser");
    if (s.opaque > 0) out.push(`${s.opaque} cross-origin (no Timing-Allow-Origin)`);
    if (s.image.pending > 0) out.push(`${s.image.pending} bg images sizing`);
    if (s.audio.source === "none") out.push("no Howler found");
    return out;
}

/* ------------------------------------------------------------------ */
/* Singleton                                                           */
/* ------------------------------------------------------------------ */

export const perf = new MemoryProfiler();
if (typeof window !== "undefined") window.__perf = perf;

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

// Inline rather than a .module.scss, so the file stays a single drop-in with
// no build assumptions. Sizes are px, not the games' --ch units: this is
// instrumentation and should stay the same legible size whatever the host
// layout is doing.
const S = {
    overlay: {
        position: "fixed",
        // Inset for TV overscan rather than flush: sets crop the edges of the
        // viewport they render, and a Fire TV Stick Lite lost the end of every
        // line off an overlay anchored at 0. 3% is the usual title-safe margin.
        left: "3%",
        bottom: "2%",
        // 88vw, not 58. A Fire TV reports a narrower CSS viewport than its
        // 1920 Tizen counterpart and was ellipsising every note AT TV WIDTH,
        // on a TV. Width is not a safe proxy for "has room" on this estate.
        maxWidth: "88vw",
        background: "rgba(8, 10, 18, 0.88)",
        borderTop: "2px solid #4dd4ac",
        borderRight: "2px solid #4dd4ac",
        color: "#e8e8e8",
        fontFamily: "monospace",
        fontSize: 15,
        lineHeight: 1.45,
        padding: "4px 12px 6px",
        zIndex: 10000,
        pointerEvents: "none", // never steals a click from the game underneath
    },
    header: { display: "flex", gap: 12, alignItems: "baseline", marginBottom: 2 },
    title: { color: "#4dd4ac", fontWeight: "bold", letterSpacing: 1 },
    row: { display: "flex", gap: 14, flexWrap: "wrap", color: "#9fb3c8" },
    // Wrap, never clip, at ANY width. The field that used to be truncated was
    // `note`, which carries the PEAK values, so clipping was data loss rather
    // than cosmetics: current values stayed readable and every peak did not,
    // and peak is the number that matters against a ceiling. A taller panel is
    // a better failure than a missing number, on any screen.
    bucket: { display: "flex", gap: 10, whiteSpace: "normal", overflow: "visible" },
    label: { minWidth: 62, color: "#4dd4ac" },
    value: { minWidth: 76, color: "#e8e8e8", fontWeight: "bold", textAlign: "right" },
    note: { color: "#9fb3c8", overflow: "visible", textOverflow: "clip", whiteSpace: "normal" },
    // The raw UA, dim and wrapped. Long and ugly on purpose: the parsed label
    // above it is a guess, and this is the thing that settles an argument.
    ua: { color: "#6f8091", fontSize: 11, lineHeight: 1.3, whiteSpace: "normal", wordBreak: "break-all" },
    blind: { color: "#ffd93d", fontStyle: "italic" },
    warn: { color: "#ff6b6b" },
};

/* ------------------------------------------------------------------ */
/* Narrow-screen overrides                                             */
/* ------------------------------------------------------------------ */

// Shallow-merged onto S, so there is one source of truth and the wide path
// stays intact. Verified on a real 390px handset.
//
// THIS IS NOT WHAT FIXED THE CLIPPING. The first version of this block keyed
// the wrap behaviour off innerWidth <= 700, on the assumption that truncation
// was a phone problem. It is not: a Fire TV ellipsised every note at TV width,
// because it reports a narrower CSS viewport than the 1920 Tizen set does. The
// base styles now wrap at every width, and this block is left with what it
// should always have been about - type size, column widths, and the corner
// PerfToggle's button occupies. Width was never a safe proxy for "has room".

const NARROW_MAX_PX = 700;

const NARROW = {
    overlay: {
        maxWidth: "100vw",
        right: 0,
        fontSize: 11,
        lineHeight: 1.35,
        // The right padding reserves the corner PerfToggle's button sits in;
        // it renders above the panel and was covering the end of a row. If you
        // change that button's size, this and its test move together.
        padding: "4px 76px 6px 8px",
    },
    header: { gap: 8, flexWrap: "wrap" },
    row: { gap: 8 },
    // No wrap/overflow entries here any more: the BASE styles wrap at every
    // width now, because clipping turned out to happen on a Fire TV at TV
    // width, not just on phones. What is left is genuinely about small
    // screens: tighter type, tighter columns, and the reserved button corner.
    label: { minWidth: 40 },
    value: { minWidth: 52 },
};

/** Shallow-merge the narrow overrides onto the base styles. */
function styleSet(narrow) {
    if (!narrow) return S;
    const out = {};
    for (const key in S) out[key] = NARROW[key] ? { ...S[key], ...NARROW[key] } : S[key];
    return out;
}

/** True on a phone-width viewport. Re-checks on resize and rotation. */
function useNarrow() {
    const [narrow, setNarrow] = useState(
        () => (typeof window !== "undefined" ? window.innerWidth <= NARROW_MAX_PX : false)
    );
    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const check = () => setNarrow(window.innerWidth <= NARROW_MAX_PX);
        window.addEventListener("resize", check);
        window.addEventListener("orientationchange", check);
        return () => {
            window.removeEventListener("resize", check);
            window.removeEventListener("orientationchange", check);
        };
    }, []);
    return narrow;
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

/** Scale TV overlays so they take the same share of the screen on every set.
 * Sizes were tuned on a 1920-wide Tizen; Fire TV reports 960 wide. */
export function tvOverlayScale() {
    if (typeof window === "undefined") return 1;
    return Math.max(0.5, Math.min(1, window.innerWidth / 1920));
}

/** Shrink a fixed panel about its anchored corner, widening it by the same factor. */
export function scaledPanel(base, scale, origin, widthKey, widthVw) {
    if (!scale || scale === 1) return base;
    return { ...base, transform: `scale(${scale})`, transformOrigin: origin, [widthKey]: `${widthVw / scale}vw` };
}

export default function PerfOverlay() {
    const [s, setS] = useState(perf.snapshot);
    const st = styleSet(useNarrow());

    useEffect(() => {
        // Subscribe BEFORE starting, so the immediate sample that start() does
        // lands on this component rather than being missed.
        const unsubscribe = perf.subscribe(setS);
        // Guarded: a throw inside an effect propagates through the commit and
        // unmounts the tree above it, which here means the whole game screen.
        safe(() => perf.start(), null);
        return () => {
            unsubscribe();
            // Releases a reference rather than stopping outright. The last one
            // out stops the sampling; the measurements taken so far are kept,
            // so a Host that remounts between games, or a phone panel opened a
            // second time, resumes the drift fit instead of restarting it.
            safe(() => perf.release(), null);
        };
    }, []);

    const heapPct = s.heap.limit ? Math.round((s.heap.used / s.heap.limit) * 100) : 0;
    const heapWarn = s.heap.available && heapPct >= HEAP_WARN_PCT;
    const driftWarn = s.heapStale || (s.drift !== null && s.drift > DRIFT_WARN_BYTES_MIN);
    const nodeWarn = s.nodeDrift !== null && s.nodeDrift > NODE_DRIFT_WARN_PER_MIN;
    const blind = blindSpots(s);

    const audioNote =
        `${s.audio.decoded} decoded` +
        (s.audio.streaming ? ` · ${s.audio.streaming} streaming` : "") +
        (s.audio.budget ? ` · budget ${mb(s.audio.budget)}` : "") +
        ` · via ${s.audio.source}` +
        // Say the assumption out loud on the Howler path. Reported from WWTBAM:
        // ten of its music beds are mono 44.1k, so assuming stereo overstates
        // that game's audio row by about a third. Fine as an upper bound, not
        // fine if someone quotes it as the measured figure.
        (s.audio.source === "Howler" ? " (stereo assumed)" : "");

    const videoNote =
        s.video.elements === 0
            ? "no elements"
            : `${s.video.elements} el · ${mb(s.video.frameBytes)} frames + ${mb(s.video.bufferedBytes)} buffered · ` +
              s.video.detail.map((d) => `${d.name} ${d.res}`).join(", ");

    return (
        <div style={st.overlay}>
            <div style={st.header}>
                <span style={st.title}>MEM</span>
                <span style={heapWarn ? st.warn : null}>
                    heap <b>{s.heap.available ? `${mb(s.heap.used)}/${mb(s.heap.limit)} (${heapPct}%)` : "n/a"}</b>
                </span>
                <span>peak <b>{mb(s.peakHeap)}</b></span>
                <span style={driftWarn ? st.warn : null}>
                    drift{" "}
                    <b>
                        {s.heapStale
                            ? "heap frozen"
                            : s.drift === null
                                ? "settling"
                                : `${s.drift > 0 ? "+" : ""}${(s.drift / MB).toFixed(1)}MB/min`}
                    </b>
                </span>
                {/* Always shown, because it is the leak signal that works when
                    performance.memory is frozen, which is most of the time. */}
                <span style={nodeWarn ? st.warn : null}>
                    nodes{" "}
                    <b>
                        {s.nodeDrift === null
                            ? "settling"
                            : `${s.nodeDrift > 0 ? "+" : ""}${s.nodeDrift.toFixed(0)}/min`}
                    </b>
                </span>
            </div>

            <Bucket st={st} label="audio" bytes={s.audio.bytes} peak={s.peak.audio} note={audioNote} />
            <Bucket st={st} label="video" bytes={s.video.bytes} peak={s.peak.video} note={videoNote} />
            <Bucket
                st={st}
                label="image"
                bytes={s.image.bytesVisible}
                peak={s.peak.image}
                note={`${s.image.visible} on screen · ${s.image.count} known = ${mb(s.image.bytesAll)} if all decoded`}
            />
            <Bucket
                st={st}
                label="script"
                bytes={s.script.bytes}
                peak={s.peak.script}
                note={`${s.script.count} files · ${mb(s.script.encoded)} over the wire${s.wasm ? ` · wasm ${mb(s.wasm)}` : ""}`}
            />

            <div style={st.row}>
                <span>dom <b>{s.dom}</b></span>
                <span>fps <b>{s.fps}</b></span>
                <span>long tasks <b>{s.longTasks === null ? "-" : s.longTasks}</b></span>
                {s.device.ram ? <span>device ram <b>{s.device.ram}GB</b></span> : null}
                {s.device.cores ? <span>cores <b>{s.device.cores}</b></span> : null}
                <span>up <b>{Math.round(s.uptimeMs / 1000)}s</b></span>
            </div>

            {/* Identity, so a photo of this panel says which box produced it. */}
            <div style={st.row}>
                <span style={st.title}>{s.device.label}</span>
                <span>{s.device.viewport}</span>
            </div>
            {s.device.ua ? <div style={st.ua}>{s.device.ua}</div> : null}

            {blind.length > 0 && <div style={st.blind}>blind: {blind.join(" · ")}</div>}
        </div>
    );
}

/** One asset-type row: current bytes, session peak, and how it was derived. */
function Bucket({ st, label, bytes, peak, note }) {
    return (
        <div style={st.bucket}>
            <span style={st.label}>{label}</span>
            <span style={st.value}>{mb(bytes)}</span>
            <span style={st.note}>{`peak ${mb(peak)} · ${note}`}</span>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* BLIND SPOTS - read these before trusting a total                    */
/* ------------------------------------------------------------------ */
//
// 1. GPU memory is invisible. Every decoded image also gets uploaded to the
//    compositor as a texture, and nothing in the web platform reports that.
//    On a TV with shared memory the texture is as real as the bitmap, so treat
//    the image row as roughly half of what that asset actually costs.
// 2. Detached elements do not appear. An <img> or <video> React has unmounted
//    but something still holds a reference to is exactly the leak you are
//    hunting, and it is the one thing this cannot show. The heap `drift`
//    number is the instrument for that case.
// 3. AudioBuffers created outside Howler are not counted, and Howler does not
//    expose its decoded cache, so the audio figure is reconstructed from clip
//    durations rather than read.
// 4. performance.memory is per-process, Chromium-only, and mostly FROZEN.
//    Confirmed: it is deliberately degraded as an anti-side-channel measure,
//    quantised to 100KB from Chrome 86, precise only under
//    --enable-precise-memory-info. That is universal Chromium, TVs included.
//    Not confirmed: the exact period Blink holds the value for (20 minutes is
//    the figure usually quoted; I could not find it in a primary source, so
//    the overlay never states a number). Either way the heap line is a coarse
//    one-off reading rather than a live number, and the heap drift fit is
//    dead on any normally-launched browser. Hence "heap frozen" and the
//    `nodes` drift instead. Observed on a Tizen set: heap identical to 0.1MB
//    across 5.5 minutes while the DOM grew 19% and 224 long tasks fired.
// 5. Cross-origin assets report zero size unless the host sends
//    Timing-Allow-Origin. The `blind` line counts those; if it is non-zero,
//    the script and transfer figures are understated.
